import React, { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
// Styles that load first, in the same order as before the old Home page was
// split off (below): the site-wide basics (fonts, spacing reset,
// .container), then the shared follow icons and buttons. The pages' own
// styles come after these and win, e.g. the home page hiding its floating
// follow icons on phones.
import "./pages/Home/Home.css";
import "./components/SocialLinks/SocialLinks.css";
import "./components/Buttons/Buttons.css";
import Music from "./pages/Music/Music";
import SongLink from "./pages/SongLink/SongLink";
import ErrorPage from "./pages/ErrorPage/ErrorPage";
import "./index.css";
import { setUpViewTransitionFlag } from "./utils/viewTransitionFlag";
import { setUpAnalytics } from "./utils/analytics";

// The old pages are downloaded only when someone opens them, so visitors of
// the music site don't download them
const Home = lazy(() => import("./pages/Home/Home"));
const DinnerMenu = lazy(() => import("./pages/DinnerMenu/DinnerMenu"));
const CoursePage = lazy(() => import("./pages/DinnerMenu/CoursePage"));
const later = (page) => <Suspense fallback={null}>{page}</Suspense>;

// Cover for the missing blur during page transitions in Safari/Firefox (see the file)
setUpViewTransitionFlag();

// Google Analytics (live site only; see the file)
setUpAnalytics();

const router = createBrowserRouter([
  {
    path: "/",
    element: <Music />,
    errorElement: <ErrorPage />,
    // /about and /contact: the home page with that panel open (Music.js
    // reads the path; the page itself stays put underneath)
    children: [{ path: "about" }, { path: "contact" }],
  },
  {
    // Old links to /music now land on the main page
    path: "/music",
    element: <Navigate to="/" replace />,
  },
  {
    path: "/legacy/sde",
    element: later(<Home />),
    errorElement: <ErrorPage />,
  },
  {
    path: "/legacy/music",
    element: <Music legacy />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/niwin-menu",
    element: later(<DinnerMenu />),
    errorElement: <ErrorPage />,
  },
  {
    path: "/niwin-menu/:courseId",
    element: later(<CoursePage />),
    errorElement: <ErrorPage />,
  },
  {
    // Song link pages, e.g. /bucthudau (unknown songs go to /)
    path: "/:songSlug",
    element: <SongLink />,
    errorElement: <ErrorPage />,
  },
  {
    // Any unknown path goes to the main page
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);

createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
);
